/**
 * What each web pane should say about an agent (RFC 0303 3e): whether one is
 * driving it, which session, and whether the person has taken it.
 *
 * The shell drives a `<webview>` from outside the page, so the page would
 * otherwise have no way to know it is happening and the person watching would
 * see a form fill itself with no explanation. The shell tells the window after
 * every action, the window says so on the pane, and the pane offers the way
 * out.
 *
 * Take over is the SHELL's, held on the page it drives (findings 5 and 13).
 * This store only mirrors it for the banner. The buttons ask the shell
 * (`requestHold`) and change nothing here; the shell answers with the new
 * state through `notePage`. It was a set of urls kept here, and a url is the
 * wrong key: two panes share one, a pane changes its own, and the shell asked
 * by a url the window had not caught up with yet.
 *
 * Keyed by the guest's webContents id, which both ends know a pane by: the
 * shell holds the guest, and the `<webview>` element answers
 * `getWebContentsId()`.
 */

/** What the shell says about one pane. */
export interface WebPageState {
  /** Until when the pane says "Agent is driving". */
  drivingUntil: number
  /** The person has the page; the shell refuses every action on it. */
  held: boolean
  /** The session that last drove it, so the buttons reach its log. */
  sessionId?: string | null
}

const states = new Map<number, WebPageState>()
/** One expiry timer per pane, replaced on each push rather than stacked. */
const timers = new Map<number, ReturnType<typeof setTimeout>>()
const listeners = new Set<() => void>()

/**
 * What the buttons write for the shell to read. The window loads with no
 * preload, so its console is the one channel to the shell; the shell's
 * `parseHold` reads this exact prefix.
 */
export const HOLD_PREFIX = '[mod:web-hold] '

function changed(): void {
  for (const l of listeners) l()
}

/** Subscribe, the shape `useSyncExternalStore` wants. */
export function watchDriving(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The shell, after an action or a Take over: what this pane should say. */
export function notePage(webContentsId: number, state: WebPageState): void {
  if (!Number.isInteger(webContentsId)) return
  const previous = states.get(webContentsId)
  states.set(webContentsId, {
    drivingUntil: Number(state.drivingUntil) || 0,
    held: state.held === true,
    // A push without a session does not forget the one that drove it.
    sessionId: state.sessionId || previous?.sessionId || null,
  })
  changed()
  // The banner goes by itself. An agent that stops mid-errand should not leave
  // a pane claiming to be driven until the app restarts.
  const timer = timers.get(webContentsId)
  if (timer) clearTimeout(timer)
  timers.delete(webContentsId)
  const left = (Number(state.drivingUntil) || 0) - Date.now()
  if (left <= 0) return
  const next = setTimeout(() => {
    timers.delete(webContentsId)
    changed()
  }, left)
  ;(next as { unref?: () => void }).unref?.()
  timers.set(webContentsId, next)
}

/** The session that last drove this pane, or null when none is known. */
export function drivingSession(webContentsId: number): string | null {
  return states.get(webContentsId)?.sessionId ?? null
}

export function isDriving(
  webContentsId: number,
  now: number = Date.now(),
): boolean {
  const s = states.get(webContentsId)
  return !!s && !s.held && s.drivingUntil > now
}

/** Whether the shell says the person has this pane. */
export function isTakenOver(webContentsId: number): boolean {
  return states.get(webContentsId)?.held === true
}

/**
 * The person pressed Take over (`held`) or Hand back. Tells the shell, which
 * holds the latch, and changes nothing here: the banner follows the shell's
 * answer, so it never shows a take over the shell did not get.
 */
export function requestHold(webContentsId: number, held: boolean): void {
  if (!Number.isInteger(webContentsId)) return
  console.info(`${HOLD_PREFIX}${JSON.stringify({ webContentsId, held })}`)
}

/** Forget a pane: it has gone. Its guest id is never reused. */
export function forgetPage(webContentsId: number): void {
  states.delete(webContentsId)
  const timer = timers.get(webContentsId)
  if (timer) clearTimeout(timer)
  timers.delete(webContentsId)
  changed()
}
